/**
 * The card under the agent's hoist_skill call: the draft it wrote, with a
 * one-click Save to My skills (Matt, 28 Oct 2026: saving a draft took four
 * steps and left the chat). It reads the person's own skills library, so only
 * the draft's owner sees Save; anyone else reads the line alone. A skill the
 * agent saved to the organisation's skills (a Manager's hoist) is live
 * already and has nothing to save.
 */
import React, { useState } from 'react'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { CARD_SX } from '../../agent'
import useAccount from '../../hooks/useAccount'
import useRouter from '../../hooks/useRouter'
import { HOIST_WAIT_MS, useAgentSkills } from '../../hooks/useAgentSkills'

export interface SkillDraftCardProps {
  sessionId: string
  name: string
  /** The skill_hoisted event's timestamp. */
  at: string
  /** 'organisation' when a Manager's agent saved it to the organisation's skills. */
  level?: string
}

const SkillDraftCard: React.FC<SkillDraftCardProps> = ({ sessionId, name, at, level }) => {
  const account = useAccount()
  const router = useRouter()
  const customer = (router.params.customer as string) || account.customer?.name || ''
  const organisation = level === 'organisation'
  // Wait for the catalog only while the hoist is new; an old card reads once.
  const recent = Boolean(at) && Date.now() - Date.parse(at) < HOIST_WAIT_MS
  const skills = useAgentSkills({ customer, sessionId, hoisted: recent ? { name, at } : null, enabled: !organisation })
  const [saving, setSaving] = useState(false)

  const draft = skills.drafts.find((d) => d.name === name)
  const saved = skills.mine.find((m) => m.name === name)

  const handleSave = async () => {
    if (!draft) return
    setSaving(true)
    try {
      await skills.handlePublish(draft.id)
    } catch {
      // useAgentSkills reports the failure; the draft stays a draft.
    } finally {
      setSaving(false)
    }
  }

  let line: string
  if (organisation) line = `${name} is saved to the organisation's skills. Every colleague's new chats have it.`
  else if (draft) line = `Draft skill ${name}. Only you can see it until you save it to My skills.`
  else if (saved) line = `${name} is saved to My skills.`
  else line = `Draft skill ${name}. Find it in the Skills panel.`

  return (
    <Box
      data-testid="skill-draft-card"
      sx={{ ...CARD_SX, my: 1, p: 1.5, display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}
    >
      <AutoAwesomeOutlinedIcon fontSize="small" color="info" />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{line}</Typography>
      {draft && !organisation && (
        <Tooltip title={saved ? `It replaces your saved skill ${name}.` : 'Edit or test it first in the Skills panel, if you want to.'}>
          <span>
            <Button size="small" variant="contained" color="info" disableElevation disabled={saving} onClick={() => { void handleSave() }}>
              Save to My skills
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  )
}

export default SkillDraftCard
